import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'src/app/core/shared/http.service';
import {
  BatteryLoadConfigValue,
  BatteryType,
  CartridgeConfigPort,
  FuelcellBatteryConfigData,
  FuelcellCartridgeData,
  FuelcellChannelAddress,
  FuelcellDetails,
  FuelcellPortLevel,
  FuelcellSettings,
  FuelcellSystemStateData,
  SystemStateChangeActionType
} from '../interfaces/fuelcell-data.interface';

/**
 * Reads the current data of a fuel cell and sends commands to it.
 * All endpoints are addressed with the route station and the DE channel of the fuel cell.
 */
@Injectable({
  providedIn: 'root'
})
export class FuelcellService {

  constructor(private readonly httpService: HttpService) { }

  /** Latest system state message and device data of the fuel cell */
  public getDetails(target: FuelcellChannelAddress): Observable<FuelcellDetails> {
    return this.httpService.get<FuelcellDetails>('Tls/FuelCell/Details' + query(target));
  }

  public getSystemState(target: FuelcellChannelAddress): Observable<FuelcellSystemStateData> {
    return this.httpService.get<FuelcellSystemStateData>('Tls/FuelCell/SystemState' + query(target));
  }

  public getCartridgeData(target: FuelcellChannelAddress): Observable<FuelcellCartridgeData> {
    return this.httpService.get<FuelcellCartridgeData>('Tls/FuelCell/CartridgeData' + query(target));
  }

  public getBatteryConfigData(target: FuelcellChannelAddress): Observable<FuelcellBatteryConfigData> {
    return this.httpService.get<FuelcellBatteryConfigData>('Tls/FuelCell/BatteryConfigData' + query(target));
  }

  public getSettings(target: FuelcellChannelAddress): Observable<FuelcellSettings> {
    return this.httpService.get<FuelcellSettings>('Tls/FuelCell/IgnoreExternalFuelSensor' + query(target));
  }

  /** Only stored in the database, no command to the fuel cell */
  public setIgnoreExternalFuelSensor(target: FuelcellChannelAddress, ignoreExternalFuelSensor: boolean): Observable<FuelcellSettings> {
    return this.httpService.put<FuelcellSettings>('Tls/FuelCell/IgnoreExternalFuelSensor',
      { ...address(target), ignoreExternalFuelSensor: ignoreExternalFuelSensor });
  }

  public changeSystemStatus(target: FuelcellChannelAddress, changeStatusAction: SystemStateChangeActionType): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ChangeSystemStatus',
      { ...address(target), changeStatusAction: changeStatusAction });
  }

  /** Fill levels of one or more ports in one command */
  public changeFillLevels(target: FuelcellChannelAddress, portLevels: FuelcellPortLevel[]): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ChangeFillLevel', { ...address(target), portLevels: portLevels });
  }

  public activatePort(target: FuelcellChannelAddress, portNumber: number): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ActivatePort', { ...address(target), portNumber: portNumber });
  }

  public changeCartridgeConfig(target: FuelcellChannelAddress, cartridgesConfig: CartridgeConfigPort[]): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ChangeCartridgeConfig',
      { ...address(target), cartridgesConfig: cartridgesConfig });
  }

  public changeBatteryType(target: FuelcellChannelAddress, batType: BatteryType): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ChangeBatteryType', { ...address(target), batType: batType });
  }

  public changeBatteryLoadConfig(target: FuelcellChannelAddress, loadConfig: BatteryLoadConfigValue[]): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ChangeBatteryLoadConfig', { ...address(target), loadConfig: loadConfig });
  }

  public resetBatteryConfig(target: FuelcellChannelAddress): Observable<any> {
    return this.httpService.post('Tls/FuelCell/ResetBatteryConfig', address(target));
  }
}

/** Only the addressing, no further properties of the given object */
function address(target: FuelcellChannelAddress): FuelcellChannelAddress {
  return { id: target.id, deChannel: target.deChannel };
}

function query(target: FuelcellChannelAddress): string {
  return '?id=' + encodeURIComponent(target.id) + '&deChannel=' + target.deChannel;
}
